import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'

export const useAuthStore = defineStore('auth', () => {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently, logout: auth0Logout } = useAuth0()

  const roles = computed<string[]>(() => {
    const claims = user.value?.['https://storyframe.io/roles']
    if (!claims) return []
    if (typeof claims === 'string') return claims.split(',').map((r: string) => r.trim())
    if (Array.isArray(claims)) return claims
    return []
  })

  const isAdmin = computed(() => roles.value.some(r => r.toLowerCase() === 'admin'))
  const isEditor = computed(() => roles.value.some(r => r.toLowerCase() === 'editor'))
  const canDelete = computed(() => isAdmin.value)
  const canArchive = computed(() => isAdmin.value)
  const canPublish = computed(() => isAdmin.value || isEditor.value)

  const userEmail = computed(() => user.value?.email ?? '')
  const userName = computed(() => user.value?.name ?? userEmail.value)
  const userInitials = computed(() => {
    const name = userName.value
    if (!name) return '?'
    const parts = name.split(' ')
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return name[0].toUpperCase()
  })

  async function getToken(): Promise<string> {
    return await getAccessTokenSilently()
  }

  function logout() {
    auth0Logout({ logoutParams: { returnTo: window.location.origin } })
  }

  return {
    user,
    isAuthenticated,
    isLoading,
    roles,
    isAdmin,
    isEditor,
    canDelete,
    canArchive,
    canPublish,
    userEmail,
    userName,
    userInitials,
    getToken,
    logout,
  }
})
